/**
 * <s-consent-gate> — requires the buyer to accept the store policies before buying.
 *
 * The checkbox inside ([data-consent-checkbox]) is a native `required` field, so the
 * surrounding product or cart form cannot submit until it is checked. Accelerated
 * checkout buttons skip form validation, so every [data-consent-gated] region stays
 * inert until the box is checked. Acceptance is remembered for the browser session so
 * partial re-renders (variant change, cart refresh) do not clear the buyer's own choice.
 */
const STORAGE_KEY = 'purchase-consent-accepted';

class SConsentGate extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const checkbox = this.#checkbox();
    if (!checkbox) return;

    if (this.#remembered()) checkbox.checked = true;
    this.#sync();

    checkbox.addEventListener('change', () => {
      this.#remember(checkbox.checked);
      this.#sync();
    }, { signal: this.#ac.signal });

    checkbox.addEventListener('invalid', () => {
      checkbox.setCustomValidity(this.dataset.requiredMessage || '');
      this.querySelector('[data-consent-error]')?.removeAttribute('hidden');
    }, { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #checkbox() {
    return this.querySelector('[data-consent-checkbox]');
  }

  #sync() {
    const checkbox = this.#checkbox();
    const accepted = Boolean(checkbox?.checked);
    if (accepted) {
      checkbox.setCustomValidity('');
      this.querySelector('[data-consent-error]')?.setAttribute('hidden', '');
    }
    this.querySelectorAll('[data-consent-gated]').forEach((region) => {
      region.inert = !accepted;
      region.classList.toggle('opacity-50', !accepted);
    });
  }

  #remembered() {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  #remember(value) {
    try {
      sessionStorage.setItem(STORAGE_KEY, value ? 'true' : 'false');
    } catch {
      // Storage unavailable: the buyer simply checks the box again after a refresh.
    }
  }
}

if (!customElements.get('s-consent-gate')) customElements.define('s-consent-gate', SConsentGate);
